import clsx from 'clsx';
import { useState, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useLocation, useNavigate } from 'react-router-dom';

import Loader from 'components/loader';
import NotFound from 'components/not-found';
import TabsScreen from 'components/tabs-screen';
import { ButtonProps } from 'components/button';
import RadioButton from 'components/radio-button';
import ItemsYmap, { MapItem } from 'components/items-ymap';
import BuildingBottomSheet from 'components/building-bottom-sheet';

import useBoundStore from 'store';
import useAppMode from 'hooks/use-app-mode';
import { SellApartmentFlowPaths } from 'routes';
import useApartmentComplexBuildings from 'api/hooks/use-apartment-complex-buildings';

import styles from './styles.module.scss';

export type ChooseBuildingNavProps = {
  defaultTab: number;
};

const ChooseBuilding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const defaultTab = (location.state as ChooseBuildingNavProps | undefined)?.defaultTab;

  const selectedSellBuilding = useBoundStore((s) => s.selectedSellBuilding);

  const [tab, setTab] = useState(0);

  const buttons: ButtonProps[] = [
    {
      label: 'Продолжить',
      disabled: selectedSellBuilding.id === -1,
      onClick: () =>
        navigate(SellApartmentFlowPaths.inputApartmentNumber, { state: { screen: 'map' } }),
    },
  ];

  return (
    <TabsScreen
      buttons={buttons}
      onTabChange={setTab}
      title='Выберите дом'
      noMarginForFloatingButtons
      fixedContentHeight={tab === 1}
      footerClassName={styles.footer}
      tabs={[
        { title: 'На карте', content: <ChooseBuildingMap /> },
        { title: 'Списком', content: <ChooseBuildingList /> },
      ]}
      onBackButtonClick={() => navigate(SellApartmentFlowPaths.chooseApartmentComplex)}
      defaultTab={defaultTab}
    />
  );
};

/* ------------------------------------------------------------------------------------ */

const ChooseBuildingMap: React.FC = () => {
  const appMode = useAppMode();

  const { buildings } = useApartmentComplexBuildings();

  const selectedComplexes = useBoundStore((s) => s.selectedComplexes);
  const selectedSellBuilding = useBoundStore((s) => s.selectedSellBuilding);

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [bottomSheetBuildingId, setBottomSheetBuildingId] = useState(0);

  const handleOpenBottomSheet = (id: number) => {
    setIsBottomSheetOpen(true);
    setBottomSheetBuildingId(id);
  };

  const mapItems = useMemo(
    (): MapItem[] =>
      buildings.map((building) => ({
        id: building.id,
        selected:
          appMode === 'buy'
            ? selectedComplexes.has(building.id)
            : selectedSellBuilding.id === building.id,
        coords: [
          56.01008 + (Math.random() * 2 - 1) / 100,
          92.838567 + (Math.random() * 2 - 1) / 100,
        ],
      })),
    [buildings, appMode, selectedComplexes, selectedSellBuilding.id],
  );

  return (
    <>
      <div className={styles.tabContainer}>
        <ItemsYmap items={mapItems} onItemClick={(item) => handleOpenBottomSheet(item.id)} />
      </div>

      <BuildingBottomSheet
        open={isBottomSheetOpen}
        buildingId={bottomSheetBuildingId}
        onDismiss={() => setIsBottomSheetOpen(false)}
        onSpringEnd={(e) => e.type === 'CLOSE' && setBottomSheetBuildingId(0)}
      />
    </>
  );
};

/* ------------------------------------------------------------------------------------ */

const ChooseBuildingList: React.FC = () => {
  const selectedSellBuilding = useBoundStore((s) => s.selectedSellBuilding);
  const setSelectedSellBuilding = useBoundStore((s) => s.setSelectedSellBuilding);

  const { buildings, isLoading } = useApartmentComplexBuildings();

  return (
    <div className={styles.tabContainerList}>
      {buildings.length > 0 ? (
        <Virtuoso
          useWindowScroll
          data={buildings}
          style={{ height: '100%' }}
          itemContent={(idx, i) => {
            return (
              <div className={styles.mainContainer}>
                <div className={styles.commissioningQuarterContainer}>
                  <div className={styles?.buildingTitle}>
                    <p>{i.title}</p>
                  </div>
                  <div className={styles.commissioningQuarter}>
                    {`Срок сдачи:  ${i.commissioningQuarter}`}
                  </div>
                </div>

                <RadioButton
                  label={''}
                  className={clsx(
                    styles.checkbox,
                    idx === buildings.length - 1 && styles.checkboxLast,
                  )}
                  labelPosition='left'
                  checked={selectedSellBuilding.id === i.id}
                  onChange={() => setSelectedSellBuilding({ id: i.id, title: i.title })}
                />
              </div>
            );
          }}
        />
      ) : isLoading ? (
        <Loader />
      ) : (
        <NotFound />
      )}
    </div>
  );
};

export default ChooseBuilding;
